import { cardFees } from '@perpay-web/fintech/constants/cardFeesConstants';
import { getClassName } from '@perpay-web/utils/domUtils';
import { formatCurrency } from '@perpay-web/utils/stringUtils';

import styles from './SchumerBox.scss';

// these values may change from time to time, so keeping
// them here easier swapping
const dailyPeriodicRate = '0.07600%';
const variableAprAccurateAsOfDate = 'December 11, 2025';
const apr = '27.74%';
const variableApr = '20.99%';
const metalCardFeeString = formatCurrency(cardFees.METAL_CARD_FEE, true, true);

const InterestRateTable = () => (
    <table className={styles.table}>
        <thead>
            <tr className={styles.row}>
                <th colSpan='2' className={styles.header}>
                    Interest Rate and Interest Charges
                </th>
            </tr>
        </thead>
        <tbody>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Annual Percentage Range (APR)</strong>
                </td>
                <td className={styles.cell}>
                    <span className={styles.attention}>{apr}</span>. This APR
                    will vary with the market based on Prime Rate*.
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Paying Interest</strong>
                </td>
                <td className={styles.cell}>
                    Your due date is at least 23 days after the close of each
                    billing cycle. We will not charge you interest on new
                    purchases if you pay your entire balance by the due date
                    each month.
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Minimum Interest Charge</strong>
                </td>
                <td className={styles.cell}>
                    If you are charged interest, the charge will be no less than
                    $1.00.
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>
                        For Credit Card Tips from the Consumer Financial
                        Protection Bureau
                    </strong>
                </td>
                <td className={styles.cell}>
                    <strong>
                        To learn more about factors to consider when applying
                        for or using a credit card, visit the website of the
                        Consumer Financial Protection Bureau at:
                        http://www.consumerfinance.gov/learnmore
                    </strong>
                </td>
            </tr>
        </tbody>
    </table>
);

const FeesTable = () => (
    <table className={styles.table}>
        <thead>
            <tr className={styles.row}>
                <th colSpan='2' className={styles.header}>
                    Fees
                </th>
            </tr>
        </thead>
        <tbody>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Annual Fee</strong>
                </td>
                <td className={styles.cell}>
                    <strong>$0</strong>
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Account Opening Fee</strong>
                </td>
                <td className={styles.cell}>
                    <strong>$9.00</strong> (one-time fee)
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Monthly Account Service Fee</strong>
                </td>
                <td className={styles.cell}>
                    <strong>$66 to $108</strong> for the first year ($5.50 to
                    $9.00 per month) and <strong>$108</strong> thereafter ($9.00
                    per month)
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Metal Card Fee (Optional)</strong>
                </td>
                <td className={styles.cell}>
                    <strong>{metalCardFeeString}</strong> (one-time fee)
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Transaction Fees</strong>
                    <ul className={styles.list}>
                        <li>Foreign Transaction Fee</li>
                    </ul>
                </td>
                <td className={styles.cell}>
                    <ul
                        className={getClassName(
                            styles.list,
                            styles.aligned,
                            styles.large,
                        )}
                    >
                        <li>
                            <strong>3%</strong> of each transaction in U.S.
                            dollars.
                        </li>
                    </ul>
                </td>
            </tr>
            <tr className={styles.row}>
                <td className={styles.cell}>
                    <strong>Penalty Fees</strong>
                    <ul className={styles.list}>
                        <li>Late Payment</li>
                        <li>Returned Payment</li>
                        <li>Over-the-credit-limit</li>
                    </ul>
                </td>
                <td className={styles.cell}>
                    <strong>
                        <ul
                            className={getClassName(
                                styles.list,
                                styles.aligned,
                            )}
                        >
                            <li>Up to $24.00</li>
                            <li>Up to $24.00</li>
                            <li>None</li>
                        </ul>
                    </strong>
                </td>
            </tr>
        </tbody>
    </table>
);

const AdditionalContent = () => (
    <div>
        <p className={styles.paragraph}>
            <strong>How We Will Calculate Your Balance:</strong> We use a method
            called “average daily balance” (including new transactions). See the
            Consumer Card Agreement below for more details.
        </p>
        <p className={styles.paragraph}>
            <strong>Billing Rights:</strong> Information on your rights to
            dispute transactions and how to exercise those rights is provided in
            the Consumer Card Agreement below.
        </p>
        <p className={styles.paragraph}>
            <strong>How Do You Calculate Variable APRs?</strong> The variable
            APR disclosed above is based on the Prime Rate + Margin (
            {variableApr}). If the Prime Rate changes, the variable APR may
            increase or decrease. If the variable APR increases, your interest
            charges and your minimum payment may increase. We calculate the
            variable APR by adding a fixed percentage (known as the “Margin”) to
            the Prime Rate published in The Wall Street Journal two Business
            Days before the end of the Billing Cycle. If the Wall Street Journal
            does not publish the Prime Rate on that day, we will use the Prime
            Rate for the previous Business Day. If The Wall Street Journal does
            not publish the Prime Rate or changes the definition of the Prime
            Rate, we may use the Prime Rate from any other newspaper. Or, we may
            choose to use a different, similar published rate. If the Prime Rate
            changes, the resulting changes to the variable APR take effect as of
            the first day of the next Billing Cycle.{' '}
        </p>
        <p className={styles.paragraph}>
            *Variable APR is accurate as of {variableAprAccurateAsOfDate}.
        </p>
        <p className={styles.paragraph}>
            <strong>
                What Are The Daily Periodic Rates Used To Calculate My Interest?
            </strong>{' '}
            The daily periodic rate for your Purchase APR is currently{' '}
            {dailyPeriodicRate}.
        </p>
        <p className={styles.paragraph}>
            <strong>Note:</strong> Your Account is not eligible for balance
            transfers or Cash Advances.
        </p>
        <p className={styles.paragraph}>
            <strong>Military Lending Act Notice:</strong> Federal law provides
            important protections to members of the Armed Forces and their
            dependents relating to extensions of consumer credit. In general,
            the cost of consumer credit to a member of the Armed Forces and his
            or her dependent may not exceed an annual percentage rate of 36
            percent. This rate must include, as applicable to the credit
            transaction or account: the costs associated with credit insurance
            premiums; fees for ancillary products sold in connection with the
            credit transaction; any application fee charged (other than certain
            application fees for specified credit transactions or accounts); and
            any participation fee charged (other than certain participation fees
            for a credit card account). To receive this information and a
            description of your payment obligation, please call 1-866-953-0189.
        </p>
    </div>
);

const SchumerBox = ({ className }) => (
    <div className={getClassName(className, styles.container)}>
        <InterestRateTable />
        <FeesTable />
        <AdditionalContent />
    </div>
);

export default SchumerBox;
